import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { post, get } from "../lib/api";
import { capture, type InputMethod } from "../lib/analytics";
import { GildedFrame, PrimaryButton, QuietButton, Muted, ErrorText, Spinner } from "./ui";

// "How do you want to add your photo?"
//
// The sale is already done and the credit is on the account, so none of these is
// a funnel or margin fork — they're three conveniences, and the only thing that
// separates them is INPUT QUALITY. That's why QR leads: the phone has the better
// camera and the natural selfie posture, and a better input is a better gallery
// on a sale we've already made.

interface Props {
  packId: string;
  onFile: (file: File, method: InputMethod) => void;
}

export function InputChooser({ packId, onFile }: Props) {
  const [mode, setMode] = useState<"choose" | "webcam" | "qr">("choose");

  useEffect(() => {
    capture("inputChooserViewed", { packId });
  }, [packId]);

  if (mode === "webcam") return <WebcamCapture onCancel={() => setMode("choose")} onFile={(f) => onFile(f, "webcam")} />;
  if (mode === "qr") return <QrHandoff packId={packId} onCancel={() => setMode("choose")} />;

  return (
    <div className="mx-auto max-w-2xl">
      <h2 className="text-center font-serif text-2xl text-ivory">How do you want to add your photo?</h2>
      <Muted className="mb-5 mt-1 text-center">Your credit is already saved. This is just about getting the best shot.</Muted>

      <div className="grid gap-3 sm:grid-cols-3">
        {/* QR first and visually loudest — quality insurance on a sale already made. */}
        <button
          onClick={() => {
            capture("inputMethodChosen", { method: "qr", packId });
            setMode("qr");
          }}
          className="text-left sm:order-1"
        >
          <div className="h-full rounded-xl border-2 border-gold/60 bg-gold/[0.06] p-4 transition hover:border-gold">
            <div className="text-2xl">📱</div>
            <div className="mt-2 font-serif text-lg text-ivory">Use my phone</div>
            <Muted className="mt-1 text-[12px]">
              Scan a code and shoot on your phone's better camera. Best results.
            </Muted>
            <div className="mt-2 inline-block rounded-full bg-gold/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-goldBright">
              Recommended
            </div>
          </div>
        </button>

        <label className="cursor-pointer text-left sm:order-2">
          <div className="h-full rounded-xl border border-gold/25 bg-cardInk p-4 transition hover:border-gold/50">
            <div className="text-2xl">🖼️</div>
            <div className="mt-2 font-serif text-lg text-ivory">Upload a photo</div>
            <Muted className="mt-1 text-[12px]">A good photo you already have often beats a live one.</Muted>
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                capture("inputMethodChosen", { method: "upload", packId });
                onFile(f, "upload");
              }
            }}
          />
        </label>

        <button
          onClick={() => {
            capture("inputMethodChosen", { method: "webcam", packId });
            setMode("webcam");
          }}
          className="text-left sm:order-3"
        >
          <div className="h-full rounded-xl border border-gold/25 bg-cardInk p-4 transition hover:border-gold/50">
            <div className="text-2xl">💻</div>
            <div className="mt-2 font-serif text-lg text-ivory">Use this webcam</div>
            <Muted className="mt-1 text-[12px]">Quick, but laptop cameras are the weakest option.</Muted>
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Webcam ──────────────────────────────────────────────────────────
// The weakest input, so it gets the most coaching. The captured frame goes
// through the identical server safety screen as an upload — pointing a webcam at
// someone else's face, or at a photo on a screen, is exactly as easy as
// uploading one, and the server treats them the same.

function WebcamCapture({ onFile, onCancel }: { onFile: (f: File) => void; onCancel: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch {
        setErr("We couldn't reach your camera. Check browser permissions, or upload a photo instead.");
      }
    })();
    // Always release the camera — a live light after you've left is alarming.
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function shoot() {
    const v = videoRef.current;
    if (!v) return;
    // Square crop from the centre: the models want a face, not a room.
    const side = Math.min(v.videoWidth, v.videoHeight);
    const cv = document.createElement("canvas");
    cv.width = cv.height = side;
    const ctx = cv.getContext("2d")!;
    ctx.translate(side, 0);
    ctx.scale(-1, 1); // un-mirror: the preview is mirrored, the photo shouldn't be
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, side, side);
    cv.toBlob(
      (blob) => {
        if (!blob) return setErr("Couldn't capture that frame. Please try again.");
        streamRef.current?.getTracks().forEach((t) => t.stop());
        onFile(new File([blob], "webcam.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <GildedFrame>
        <div className="relative overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} playsInline muted className="aspect-square w-full -scale-x-100 object-cover" />
          {/* Framing guide — the single biggest lever on webcam input quality. */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-[68%] w-[52%] rounded-[50%] border-2 border-gold/70" />
          </div>
          {!ready && !err && (
            <div className="absolute inset-0 flex items-center justify-center bg-screenInk/80">
              <Spinner />
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="mb-3 space-y-1">
            <Muted className="text-[12px]">• Put your face inside the oval, filling most of it.</Muted>
            <Muted className="text-[12px]">• Face a window or lamp. Light on your face, not behind you.</Muted>
            <Muted className="text-[12px]">• Just you in frame, looking at the camera.</Muted>
          </div>
          <ErrorText>{err}</ErrorText>
          <PrimaryButton onClick={shoot} disabled={!ready}>
            Take the photo
          </PrimaryButton>
          <div className="mt-2 flex justify-center">
            <QuietButton onClick={onCancel}>Back</QuietButton>
          </div>
        </div>
      </GildedFrame>
    </div>
  );
}

// ── QR handoff ──────────────────────────────────────────────────────

interface CaptureState {
  status: "waiting" | "scanned" | "generating" | "ready" | "failed";
  packId: string;
  error?: string;
}

function QrHandoff({ packId, onCancel }: { packId: string; onCancel: () => void }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [state, setState] = useState<CaptureState["status"]>("waiting");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let stop = false;
    let sessionId = "";

    void (async () => {
      try {
        const res = await post<{ url: string; sessionId: string }>("/v1/capture/token", { packId });
        sessionId = res.sessionId;
        setDataUrl(
          await QRCode.toDataURL(res.url, {
            width: 320,
            margin: 1,
            color: { dark: "#E7C976", light: "#0C0805" },
          }),
        );

        // The laptop waits here while the phone works. The gallery itself lands
        // in the account, so this poll is only about showing the right thing —
        // if the user closes this tab, nothing is lost.
        const poll = async () => {
          if (stop) return;
          try {
            const s = await get<CaptureState>(`/v1/capture/state?sessionId=${sessionId}`);
            setState(s.status);
            if (s.status === "failed") setErr(s.error || "Something went wrong on your phone.");
            if (s.status === "ready") {
              capture("qrCaptureCompleted", { packId });
              window.location.href = "/gallery";
              return;
            }
          } catch {
            /* a dropped poll is not worth surfacing; try again */
          }
          setTimeout(() => void poll(), 2500);
        };
        setTimeout(() => void poll(), 2500);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Couldn't create a code.");
      }
    })();

    return () => {
      stop = true;
    };
  }, [packId]);

  const label: Record<CaptureState["status"], string> = {
    waiting: "Scan with your phone's camera",
    scanned: "Phone connected. Take your selfie there.",
    generating: "Making your portraits…",
    ready: "Done. Opening your gallery…",
    failed: "That didn't work",
  };

  return (
    <div className="mx-auto max-w-md text-center">
      <GildedFrame>
        <div className="p-5">
          <h2 className="font-serif text-2xl text-ivory">{label[state]}</h2>
          <Muted className="mt-1">
            {state === "waiting"
              ? "Point your phone camera at this code. Your credit and this era come with it."
              : "Keep this page open. Your portraits will appear here too."}
          </Muted>

          <div className="my-5 flex justify-center">
            {dataUrl ? (
              <img src={dataUrl} alt="Scan to capture on your phone" className="rounded-lg" width={256} height={256} />
            ) : (
              <Spinner />
            )}
          </div>

          {state !== "waiting" && state !== "failed" && (
            <div className="mb-3 flex items-center justify-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-gold" />
              <Muted>Waiting for your photo…</Muted>
            </div>
          )}

          <ErrorText>{err}</ErrorText>
          <Muted className="text-[11px]">The code expires in 10 minutes and works once.</Muted>
          <div className="mt-3 flex justify-center">
            <QuietButton onClick={onCancel}>Use another method</QuietButton>
          </div>
        </div>
      </GildedFrame>
    </div>
  );
}
